import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MetaTags from 'components/common/MetaTags';
import AdminDataTable from 'components/admin/AdminDataTable';
import MainContent from 'components/NewDesign/MainContent';
import PageHeader from 'components/NewDesign/PageHeader';
import { deleteClubAgeGroupById, getClubAgeGroups } from 'api/club/ageGroupApi';
import CustomNewButton from 'components/NewDesign/CustomNewButton/CustomNewButton';
import styles from './AgeGroups.module.css';
import AgeGroupEditPopup from 'components/admin/ClubSettings/AgeGroupEditPopup';
import AgeGroupDeletionImpactDialog from 'components/admin/ClubSettings/AgeGroupDeletionImpactDialog';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import { getErrorMessage } from 'utils/api';
import { toast } from 'react-toastify';
import TableActionButtons from 'components/NewDesign/TableActionButtons';
import {
  groupingBasisOf,
  groupWords,
  formatGroupRange,
} from 'utils/groupLabel';

// words: what this club calls its groups (Age Group or Year Group), from
// groupWords. The range column shows ageRangeText, the stored range as the
// club reads it ("U10-U11", or "Y5-Y6" for a club that groups by year level).
const ageGroupColumns = (words, onEdit, onDelete) => [
  {
    accessor: 'ageGroupName',
    label: `${words.group} Name`,
  },
  {
    accessor: 'ageRangeText',
    label: `${words.value} Range`,
  },
  {
    accessor: 'genderSplitText',
    label: 'Gender Split',
  },
  {
    accessor: 'sortOrder',
    label: 'Sort Order',
  },
  {
    accessor: 'visibleText',
    label: 'Visible',
  },
  {
    accessor: 'inUseText',
    label: 'In Use',
  },
  {
    accessor: 'actions',
    label: 'Actions',
    Cell: ({ row }) =>
      TableActionButtons(row, onEdit, onDelete, {
        editTitle: `Edit ${words.lower.group}`,
        deleteTitle: `Delete ${words.lower.group}`,
      }),
  },
];

const AgeGroups = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const groupingBasis = groupingBasisOf(selectedClub);
  const words = groupWords(groupingBasis);
  const [ageGroups, setAgeGroups] = useState([]);
  const [fetchingGroups, setFetchingGroups] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState();
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editMode, setEditMode] = useState();

  // The page's one filter (Doug, 2026-10-29: "The only filter we need is the
  // Show Hidden check box like we do in event types"): ticked when the page
  // opens, as on Event Types; unticked, groups whose Visible is No are left out.
  const [showHidden, setShowHidden] = useState(true);
  const shownGroups = useMemo(
    () => (showHidden ? ageGroups : ageGroups.filter((group) => group.isVisible)),
    [ageGroups, showHidden]
  );

  // Delete confirmation state — the initial delete may come back 409 with an
  // impact report naming the non-completed meets that use the group
  // (age-group-lifecycle-design.md §5.2/§7.3); the report dialog resubmits
  // with the acknowledged meet IDs.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImpactDialog, setShowImpactDialog] = useState(false);
  const [impactReport, setImpactReport] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAgeGroups = useCallback(async () => {
    try {
      setFetchingGroups(true);
      const response = await getClubAgeGroups(selectedClub?.clubID);
      if (response.data) {
        setAgeGroups(
          response.data.map((group) => {
            return {
              ...group,
              // ageRange stays as stored: the edit dialog converts it itself.
              ageRangeText: formatGroupRange(groupingBasis, group.ageRange),
              genderSplitText: group.genderSplit ? 'Yes' : 'No',
              visibleText: group.isVisible ? 'Yes' : 'No',
              inUseText: group.isInUse
                ? `Yes (${group.meetUsageCount} ${group.meetUsageCount === 1 ? 'meet' : 'meets'})`
                : 'No',
            };
          })
        );
      }
    } catch (err) {
      console.log('[AgeGroups] Fetch age groups error: ', err);
    }
  }, [selectedClub?.clubID, groupingBasis]);

  useEffect(() => {
    fetchAgeGroups().then(() => {
      setFetchingGroups(false);
    });
  }, [fetchAgeGroups]);

  const handleAddAgeGroup = () => {
    setShowEditPopup(true);
    setEditMode('add');
  };

  const handleEditAgeGroup = (group) => {
    setShowEditPopup(true);
    setEditMode('edit');
    setSelectedGroup(group);
  };

  const handleDeleteAgeGroup = (group) => {
    setSelectedGroup(group);
    setShowDeleteConfirm(true);
  };

  const handleEditPopupClose = async (refresh) => {
    setShowEditPopup(false);
    setEditMode(null);
    setSelectedGroup(null);
    if (refresh) {
      await fetchAgeGroups();
      setFetchingGroups(false);
    }
  };

  const closeDeleteDialogs = () => {
    setShowDeleteConfirm(false);
    setShowImpactDialog(false);
    setImpactReport(null);
    setSelectedGroup(null);
  };

  const performDeleteGroup = async (acknowledgedMeetIds) => {
    if (!selectedClub?.clubID || !selectedGroup) {
      return;
    }
    try {
      setIsDeleting(true);
      await deleteClubAgeGroupById(
        selectedClub.clubID,
        selectedGroup.ageGroupID,
        acknowledgedMeetIds
      );
      toast.success(`${words.sentence.group} deleted successfully`);
      closeDeleteDialogs();
      await fetchAgeGroups();
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data) {
        // The group is used by non-completed meets — show (or refresh) the
        // impact report for explicit acknowledgment. selectedGroup is kept
        // for the confirmed resubmit.
        setImpactReport(err.response.data);
        setShowDeleteConfirm(false);
        setShowImpactDialog(true);
      } else {
        console.error(err);
        toast.error(getErrorMessage(err));
        closeDeleteDialogs();
      }
    } finally {
      setIsDeleting(false);
      setFetchingGroups(false);
    }
  };

  const confirmDeleteGroup = async () => {
    await performDeleteGroup();
  };

  const confirmDeleteGroupWithImpact = async (resolutions) => {
    await performDeleteGroup(resolutions?.acknowledgedMeetIds || []);
  };

  return (
    <>
      <MetaTags title={`SplitFast | ${words.groups}`} />
      <MainContent loading={fetchingGroups}>
        <PinProtectedAction clubId={selectedClub?.clubID}>
          {({ requestPinValidation }) => {
            // Add/edit/delete are admin PIN gated — the PIN is validated
            // server-side, then the existing popup/confirm flow proceeds.
            const addWithPin = () =>
              requestPinValidation(
                'add-age-group',
                handleAddAgeGroup,
                null,
                `Managing ${words.lower.groups} is restricted to admin users with PIN code access. Enter the admin PIN to add a new ${words.lower.group}.`
              );
            const editWithPin = (group) =>
              requestPinValidation(
                'edit-age-group',
                handleEditAgeGroup,
                group,
                `Managing ${words.lower.groups} is restricted to admin users with PIN code access. Enter the admin PIN to edit the ${words.lower.group} "${group.ageGroupName}".`
              );
            const deleteWithPin = (group) =>
              requestPinValidation(
                'delete-age-group',
                handleDeleteAgeGroup,
                group,
                `Managing ${words.lower.groups} is restricted to admin users with PIN code access. Enter the admin PIN to delete the ${words.lower.group} "${group.ageGroupName}".`
              );
            const tableColumns = ageGroupColumns(
              words,
              editWithPin,
              deleteWithPin
            );
            return (
              <>
                <PageHeader
                  title={words.groups}
                  subTitle={`Manage the ${words.lower.groups} your athletes compete in`}
                  primarySubTitle
                  regularWeightSubTitle
                >
                  <div className={styles.buttonContainer}>
                    <CustomNewButton
                      label={`+ Add ${words.group}`}
                      onClick={addWithPin}
                      style={{ marginLeft: 'auto' }}
                      variant="primary"
                    />
                  </div>
                </PageHeader>
                <AdminDataTable
                  columns={tableColumns}
                  data={shownGroups}
                  searchInputPlaceholder={`Search ${words.groups}`}
                  headerControls={
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={showHidden}
                        onChange={(e) => setShowHidden(e.target.checked)}
                      />
                      Show hidden
                    </label>
                  }
                  headStyle={{
                    gridTemplateColumns: '3fr 2fr 2fr 2fr 2fr 2fr 2fr', // name, range, gender split, sort order, visible, in use, actions
                  }}
                  rowStyle={{
                    gridTemplateColumns: '3fr 2fr 2fr 2fr 2fr 2fr 2fr', // name, range, gender split, sort order, visible, in use, actions
                  }}
                />
                <AgeGroupEditPopup
                  clubID={selectedClub?.clubID}
                  isOpen={showEditPopup}
                  onClose={(e) => handleEditPopupClose(e)}
                  group={selectedGroup}
                  mode={editMode}
                ></AgeGroupEditPopup>
                {/* Delete Confirmation Dialog — groups used by non-completed meets
                    come back 409 and continue in the impact dialog below */}
                <CustomNewConfirm
                  isOpen={showDeleteConfirm}
                  onClose={closeDeleteDialogs}
                  title={`Delete ${words.group}`}
                  description={`Are you sure you want to delete this ${words.lower.group}?`}
                  confirmButtonText={`Delete ${words.group}`}
                  confirmButtonVariant="danger"
                  onConfirm={confirmDeleteGroup}
                  cancelButtonText="Cancel"
                  showWarning={true}
                  warningText={`This can't be undone. The ${words.lower.group} is removed from your club settings and can no longer be added to meet programs`}
                  isLoading={isDeleting}
                />
                {/* Impact review — shown when the delete returns 409 with a report */}
                <AgeGroupDeletionImpactDialog
                  isOpen={showImpactDialog}
                  report={impactReport}
                  onCancel={closeDeleteDialogs}
                  onConfirm={confirmDeleteGroupWithImpact}
                  deleting={isDeleting}
                />
              </>
            );
          }}
        </PinProtectedAction>
      </MainContent>
    </>
  );
};

export default AgeGroups;
